// Store intervals and state per slider to prevent duplicates and memory leaks
const sliderState = new WeakMap();
const masonryState = new WeakMap();

export function initUI() {
  injectStyles();

  /* ---------------------------------------------------------
     CLICK HANDLERS
  --------------------------------------------------------- */
  document.addEventListener("click", (event) => {
    // Check for update elements first (before other handlers)
    const updateElement = event.target.closest("[data-mossy-update]");
    if (updateElement) {
      const targetId = updateElement.getAttribute("data-mossy-update");
      const updateKey = updateElement.getAttribute("data-mossy-update-key");
      const targetElement = document.getElementById(targetId);
      if (!targetElement || !updateKey) return;

      const newValue =
        updateElement.getAttribute("data-mossy-update-value") ||
        updateElement.getAttribute(updateKey);

      if (newValue) {
        targetElement.setAttribute(updateKey, newValue);
      }
      // Don't return here - allow other handlers to process
    }

    const toggleElement = event.target.closest("[data-mossy-toggle]");
    if (toggleElement) {
      const targetId = toggleElement.getAttribute("data-mossy-toggle");
      const targetElement = document.getElementById(targetId);
      if (targetElement) targetElement.classList.toggle("active");
      return;
    }

    const zoomElement = event.target.closest('[data-mossy-action="zoom"]');
    if (zoomElement) {
      zoomElement.classList.toggle("zoom-disabled");
      return;
    }
  });

  /* ---------------------------------------------------------
     ROUTER EVENT (RE-INIT)
  --------------------------------------------------------- */
  document.addEventListener("mossy-router", () => {
    cleanupSliders();
    cleanupMasonryGrids();
    initSliders();
    initZoomElements();
    initMasonryGrids();
    initCaptcha();
    initSelectHandlers();

    // Clear button state attributes from previous page
    clearAddToCartButtonStates();
  });

  /* ---------------------------------------------------------
     INITIAL INIT
  --------------------------------------------------------- */
  initSliders();
  initZoomElements();
  initMasonryGrids();
  initCaptcha();
  initSelectHandlers();
}

/* ---------------------------------------------------------
   INJECT DEFAULT REQUIRED STYLES
--------------------------------------------------------- */
function injectStyles() {
  const styleId = "mossy-ui-styles";
  if (document.getElementById(styleId)) return;

  const styles = `
    /* Slider basics */
    [data-mossy-indicator] {
      opacity: 0.5;
      cursor: pointer;
      transition: opacity 0.3s ease;
    }
    [data-mossy-indicator].active {
      opacity: 1;
    }
    [data-mossy-slider] {
      position: relative;
    }

    /* Lazy loading placeholder */
    [data-mossy-slide] img[data-src]:not([src]) {
      background: #f0f0f0;
      min-height: 200px;
    }

    /* Default Slide States */
    [data-mossy-slide] {
        opacity: 0;
        will-change: opacity;
        transition: opacity 0.9s ease;
        pointer-events: none;
    }
    [data-mossy-slide].active {
        opacity: 1;
        transition: opacity 0.3s ease;
        pointer-events: auto;
    }

    /* Zoomable elements */
    [data-mossy-action="zoom"] {
      cursor: zoom-out;
      transition: transform 0.3s ease;
    }
    [data-mossy-action="zoom"]:hover {
      transform: scale(2);
    }
    [data-mossy-action="zoom"].zoom-disabled {
      cursor: zoom-in;
    }
    [data-mossy-action="zoom"].zoom-disabled:hover {
      transform: none;
    }

    /* Masonry Grid (JS auto-layout) */
    [data-mossy-masonry] {
      position: relative;
    }
    [data-mossy-masonry] > * {
      position: absolute;
      top: 0;
      left: 0;
      transition: top 0.2s ease-in-out, left 0.2s ease-in-out;
    }
    [data-mossy-masonry] > *.mossy-no-transition {
      transition: none !important;
    }

    /* Form response messages */
    [data-mossy-value="response"] {
      display: none;
      margin-top: 10px;
      font-size: 14px;
      transition: opacity 0.2s ease-in-out;
    }
    [data-mossy-value="response"].show {
      display: block;
    }
  `;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = styles;
  document.head.appendChild(style);
}

/* =====================================================================
   SLIDERS
===================================================================== */

function cleanupSliders() {
  document.querySelectorAll("[data-mossy-slider]").forEach((slider) => {
    const state = sliderState.get(slider);
    if (!state) return;

    if (state.autoplayInterval) clearInterval(state.autoplayInterval);

    if (state.eventListeners) {
      state.eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
    }

    sliderState.delete(slider);
  });
}

function initSliders() {
  const sliders = document.querySelectorAll("[data-mossy-slider]");

  sliders.forEach((slider) => {
    if (sliderState.has(slider)) return;

    const slides = slider.querySelectorAll("[data-mossy-slide]");
    if (slides.length === 0) return;

    const indicators = slider.querySelectorAll("[data-mossy-indicator]");
    const autoplayDelay =
      parseInt(slider.getAttribute("data-mossy-autoplay")) || 0;

    const state = {
      currentIndex: 0,
      autoplayInterval: null,
      eventListeners: [],
      isTransitioning: false,
      isPausedByHover: false,
    };

    function addListener(el, event, fn) {
      el.addEventListener(event, fn);
      state.eventListeners.push({ element: el, event, handler: fn });
    }

    // Lazy load images in a specific slide
    function loadSlideImages(slideIndex) {
      if (slideIndex < 0 || slideIndex >= slides.length) return;

      const slide = slides[slideIndex];
      const imgs = slide.querySelectorAll("img[data-src]");

      imgs.forEach((img) => {
        if (!img.src && img.dataset.src) {
          img.src = img.dataset.src;
          // Optional: remove data-src after loading to mark as loaded
          img.removeAttribute("data-src");
        }
      });
    }

    // Preload current, previous, and next slides
    function preloadAdjacentSlides(currentIndex) {
      const prev = (currentIndex - 1 + slides.length) % slides.length;
      const next = (currentIndex + 1) % slides.length;

      // Load current slide first
      loadSlideImages(currentIndex);

      // Then preload adjacent slides
      loadSlideImages(prev);
      loadSlideImages(next);
    }

    function showSlide(index, direction = 1) {
      if (state.isTransitioning) return;
      state.isTransitioning = true;

      index = ((index % slides.length) + slides.length) % slides.length;

      slides.forEach((s, i) => s.classList.toggle("active", i === index));
      indicators.forEach((iEl, i) =>
        iEl.classList.toggle("active", i === index),
      );

      state.currentIndex = index;

      // Preload images for current and adjacent slides
      preloadAdjacentSlides(index);

      setTimeout(() => (state.isTransitioning = false), 300);
    }

    function navigate(dir, reset = true) {
      showSlide(state.currentIndex + dir);
      if (reset && autoplayDelay > 0) resetAutoplay();
    }

    function goTo(i) {
      if (i < 0 || i >= slides.length) return;
      showSlide(i);
      if (autoplayDelay > 0) resetAutoplay();
    }

    function startAutoplay() {
      if (autoplayDelay <= 0) return;
      if (state.autoplayInterval) return;

      state.autoplayInterval = setInterval(() => {
        navigate(1, false);
      }, autoplayDelay);
    }

    function stopAutoplay() {
      if (state.autoplayInterval) {
        clearInterval(state.autoplayInterval);
        state.autoplayInterval = null;
      }
    }

    function resetAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    /* Controls */
    const prevBtn = slider.querySelector("[data-mossy-prev]");
    const nextBtn = slider.querySelector("[data-mossy-next]");

    if (prevBtn) addListener(prevBtn, "click", () => navigate(-1));
    if (nextBtn) addListener(nextBtn, "click", () => navigate(1));

    indicators.forEach((iEl, idx) => {
      addListener(iEl, "click", () => goTo(idx));
    });

    addListener(slider, "keydown", (e) => {
      if (e.key === "ArrowLeft") navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
    });

    /* Swipe */
    let sx = 0,
      sy = 0;
    addListener(slider, "touchstart", (e) => {
      sx = e.changedTouches[0].screenX;
      sy = e.changedTouches[0].screenY;
    });
    addListener(slider, "touchend", (e) => {
      const dx = e.changedTouches[0].screenX - sx;
      const dy = e.changedTouches[0].screenY - sy;

      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        navigate(dx < 0 ? 1 : -1);
      }
    });

    /* Autoplay hover */
    if (autoplayDelay > 0) {
      startAutoplay();
      if (prevBtn) {
        addListener(prevBtn, "mouseenter", stopAutoplay);
        addListener(prevBtn, "mouseleave", startAutoplay);
      }
      if (nextBtn) {
        addListener(nextBtn, "mouseenter", stopAutoplay);
        addListener(nextBtn, "mouseleave", startAutoplay);
      }
    }

    showSlide(state.currentIndex);
    sliderState.set(slider, state);
  });
}

// In ui.js, add this export:
export function reinitSlider(sliderEl) {
  const state = sliderState.get(sliderEl);
  if (state) {
    if (state.autoplayInterval) clearInterval(state.autoplayInterval);
    if (state.eventListeners) {
      state.eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
    }
    sliderState.delete(sliderEl);
  }
  initSliders();
}

/* =====================================================================
   ZOOM HANDLERS
===================================================================== */

function initZoomElements() {
  const zoomElements = document.querySelectorAll('[data-mossy-action="zoom"]');

  zoomElements.forEach((element) => {
    element.classList.add("zoom-disabled");

    element.addEventListener("mousemove", (e) => {
      const r = element.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      element.style.transformOrigin = `${x}% ${y}%`;
    });

    element.addEventListener("mouseleave", () => {
      element.style.transformOrigin = "center center";
    });
  });
}

/* =====================================================================
   TRUE AUTO MASONRY (NO USER CSS REQUIRED)
===================================================================== */

/* ---------------------------------------------------------
   TRUE AUTO-MASONRY (MULTI-COLUMN, NO USER CSS REQUIRED)
--------------------------------------------------------- */

function initMasonryGrids() {
  const grids = document.querySelectorAll("[data-mossy-masonry]");

  grids.forEach((grid) => {
    if (masonryState.has(grid)) return;

    const state = {
      resizeObserver: null,
      firstLayoutDone: false,
    };

    function layout(isInitial = false) {
      const items = Array.from(grid.children);
      if (items.length === 0) return;

      const gridWidth = grid.clientWidth;
      if (gridWidth <= 0) return;

      const desiredColWidth =
        parseInt(grid.getAttribute("data-mossy-masonry-col-width")) || 300;

      const minCols = parseInt(grid.getAttribute("data-mossy-min-cols")) || 1;
      const maxCols =
        parseInt(grid.getAttribute("data-mossy-max-cols")) || Infinity;

      let cols = Math.max(1, Math.floor(gridWidth / desiredColWidth));
      cols = Math.max(minCols, Math.min(maxCols, cols));

      const colWidth = gridWidth / cols;
      const colHeights = new Array(cols).fill(0);

      // Disable transitions on first layout
      if (!state.firstLayoutDone || isInitial) {
        items.forEach((item) => item.classList.add("mossy-no-transition"));
      }

      items.forEach((item) => {
        item.style.width = colWidth + "px";

        const shortest = colHeights.indexOf(Math.min(...colHeights));

        item.style.left = shortest * colWidth + "px";
        item.style.top = colHeights[shortest] + "px";

        colHeights[shortest] += item.getBoundingClientRect().height;
      });

      grid.style.height = Math.max(...colHeights) + "px";

      // After initial layout, re-enable transitions
      if (!state.firstLayoutDone || isInitial) {
        requestAnimationFrame(() => {
          items.forEach((item) => item.classList.remove("mossy-no-transition"));
        });
        state.firstLayoutDone = true;
      }
    }

    // Resize observer
    state.resizeObserver = new ResizeObserver(() => layout(false));
    state.resizeObserver.observe(grid);

    // Reflow after images load
    const images = grid.querySelectorAll("img");
    images.forEach((img) => img.addEventListener("load", () => layout(false)));

    // First layout: no animation
    requestAnimationFrame(() => layout(true));

    masonryState.set(grid, state);
  });
}

function cleanupMasonryGrids() {
  document.querySelectorAll("[data-mossy-masonry]").forEach((grid) => {
    const state = masonryState.get(grid);
    if (!state) return;

    if (state.resizeObserver) state.resizeObserver.disconnect();

    Array.from(grid.children).forEach((item) => {
      item.style.position = "";
      item.style.left = "";
      item.style.top = "";
      item.style.width = "";
    });

    grid.style.height = "";

    masonryState.delete(grid);
  });
}

/* =====================================================================
   SELECT HANDLERS
===================================================================== */

let selectHandlerAttached = false;

function initSelectHandlers() {
  // Only attach the listener once since we're using event delegation
  if (!selectHandlerAttached) {
    document.addEventListener("change", handleSelectChange);
    selectHandlerAttached = true;
  }

  // Trigger change events for any selects that already have a value selected
  // This handles page refresh where browser remembers the selection
  initializeExistingSelections();
}

function initializeExistingSelections() {
  const selects = document.querySelectorAll("select");

  selects.forEach((select) => {
    // Check if a non-default option is selected
    const selectedOption = select.options[select.selectedIndex];

    if (
      selectedOption &&
      selectedOption.value &&
      !selectedOption.disabled &&
      selectedOption.getAttribute("data-mossy-action")
    ) {
      // Trigger the change handler for this select
      handleSelectChange({ target: select });
    }
  });
}

function handleSelectChange(event) {
  const selectElement = event.target;

  // Check if this is a select element
  if (selectElement.tagName.toLowerCase() !== "select") return;

  const selectedOption = selectElement.options[selectElement.selectedIndex];

  if (!selectedOption) return;

  const action = selectedOption.getAttribute("data-mossy-action");

  if (!action) return;

  // Parse the action (format: "update:targetId:attribute:value")
  const parts = action.split(":");
  const actionType = parts[0];
  const targetId = parts[1];
  const attribute = parts[2];
  const value = parts[3];

  if (actionType === "update" && targetId && attribute && value) {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.setAttribute(attribute, value);
      console.log(`[Mossy] Updated ${targetId}.${attribute} = ${value}`);
    } else {
      console.warn(`[Mossy] Could not find element with id: ${targetId}`);
    }
  }

  // Check if this is a variant selector in an add-to-cart form
  if (selectElement.name === "variant") {
    const form = selectElement.closest(
      'form[data-mossy-form-action="add-to-cart"]',
    );
    if (form) {
      // Recheck add-to-cart button state when variant changes
      checkAddToCartButtons();
    }
  }
}

/* =====================================================================
   CAPTCHA
===================================================================== */

async function initCaptcha() {
  const emailForms = document.querySelectorAll(
    'form[data-mossy-action="email"], form[data-mossy-action="emails_subscribe"]'
  );

  // Exit early if no email forms found
  if (emailForms.length === 0) return;

  // Only import captcha.js if there are email forms
  if (!window.Cap) {
    try {
      await import("./captcha.js");
    } catch (error) {
      console.error("Failed to load captcha module:", error);
      return;
    }
  }

  emailForms.forEach((form) => {
    // Check if captcha instance already exists to avoid duplicates
    if (!form.captchaInstance) {
      // Create invisible Cap instance for this form
      form.captchaInstance = new window.Cap({
        apiEndpoint: "https://captcha.withmossy.com/3d3cb0cddb/",
        workers: navigator.hardwareConcurrency || 8,
      });

      // Optional: Add progress listener for better UX
      form.captchaInstance.addEventListener("progress", (event) => {
        console.log(
          `[Mossy Captcha] Solving... ${event.detail.progress}% done`,
        );
      });
    }
  });
}

/* =====================================================================
   FORMS
===================================================================== */

function grabClientSideData(tag) {
  const productDataScript = document.getElementById(`mossy-data-${tag}`);
  if (productDataScript) {
    try {
      return JSON.parse(productDataScript.textContent);
    } catch (e) {
      console.error("Failed to parse mossy product data:", e);
      return null;
    }
  }
  return null;
}

/**
 * Collects values from all [data-mossy-option] fields within a form.
 * Returns an array of { id, label, type, value } objects, omitting
 * any fields the customer left blank.
 */
function collectProductOptions(form) {
  const fields = form.querySelectorAll("[data-mossy-option]");
  if (fields.length === 0) return null;

  const options = [];

  fields.forEach((field) => {
    const id = field.getAttribute("data-mossy-option");
    const label = field.getAttribute("data-mossy-option-label") || "";
    const type = field.getAttribute("data-mossy-option-type") || "text";
    const value = field.value?.trim() ?? "";

    // Skip empty / unselected fields
    if (!value) return;

    options.push({ id, label, type, value });
  });

  return options.length > 0 ? options : null;
}

function handleSuccessAction(form) {
  const successAction = form.getAttribute("data-mossy-success");
  if (!successAction) return;

  // Parse the action (format: "action:target" or "action:target:value")
  const parts = successAction.split(":");
  const action = parts[0];
  const target = parts[1];
  const value = parts[2];

  switch (action) {
    case "toggle":
      // Toggle a target element's "active" class
      if (target) {
        const targetElement = document.getElementById(target);
        if (targetElement) {
          targetElement.classList.toggle("active");
        }
      }
      break;

    case "open":
      // Add "active" class to target element
      if (target) {
        const targetElement = document.getElementById(target);
        if (targetElement) {
          targetElement.classList.add("active");
        }
      }
      break;

    case "close":
      // Remove "active" class from target element
      if (target) {
        const targetElement = document.getElementById(target);
        if (targetElement) {
          targetElement.classList.remove("active");
        }
      }
      break;

    case "navigate":
      // Navigate to a URL
      if (target) {
        // Check if we have a custom navigate function (from router)
        if (window.mossyNavigate) {
          window.mossyNavigate(target);
        } else {
          window.location.href = target;
        }
      }
      break;

    case "update":
      // Update an element's attribute (format: "update:elementId:attributeName:attributeValue")
      if (target && value) {
        const targetElement = document.getElementById(target);
        const attributeName = value;
        const attributeValue = parts[3];
        if (targetElement && attributeName) {
          targetElement.setAttribute(attributeName, attributeValue || "");
        }
      }
      break;

    case "reload":
      // Reload the page
      window.location.reload();
      break;

    case "scroll":
      // Scroll to target element
      if (target) {
        const targetElement = document.getElementById(target);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth" });
        }
      }
      break;

    default:
      console.warn(`[Mossy] Unknown success action: ${action}`);
  }
}

function clearAddToCartButtonStates() {
  const addToCartForms = document.querySelectorAll(
    'form[data-mossy-form-action="add-to-cart"]',
  );

  addToCartForms.forEach((form) => {
    const submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"]',
    );
    if (!submitButton) return;

    // Clear all our custom attributes
    submitButton.removeAttribute("data-mossy-original-text");
    submitButton.removeAttribute("data-mossy-stock-disabled");

    // Reset button state
    submitButton.disabled = false;
    submitButton.style.opacity = "1";
    submitButton.style.cursor = "pointer";
  });
}

function checkAddToCartButtons() {
  // Get cart state from window or fetch it
  const cartData = window.mossyCart?.getState?.();
  if (!cartData) return;

  const cartItems = cartData.items || [];

  // Find all add-to-cart forms
  const addToCartForms = document.querySelectorAll(
    'form[data-mossy-form-action="add-to-cart"]',
  );

  addToCartForms.forEach((form) => {
    const submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"]',
    );
    if (!submitButton) return;

    // Get product data from the page
    const product = grabClientSideData("product");
    if (!product) return;

    // Get selected or default variant
    const variantSelect = form.querySelector('select[name="variant"]');
    let selectedVariantId = null;

    if (variantSelect) {
      selectedVariantId = variantSelect.value;
    } else if (product.variants.length === 1) {
      selectedVariantId = product.variants[0].id;
    }

    if (!selectedVariantId) return;

    // Find variant data
    const variant = product.variants.find((v) => v.id === selectedVariantId);
    if (!variant) return;

    // Check if this variant is in the cart
    const cartItem = cartItems.find(
      (item) => item.variant.id === selectedVariantId,
    );
    const cartQuantity = cartItem ? cartItem.quantity : 0;
    const stockLevel = variant.stock_level;

    // Save original text on first check (before any modifications)
    if (!submitButton.hasAttribute("data-mossy-original-text")) {
      submitButton.setAttribute(
        "data-mossy-original-text",
        submitButton.textContent || submitButton.value,
      );
    }

    // Disable button if cart quantity >= stock level
    if (
      stockLevel !== null &&
      stockLevel !== undefined &&
      cartQuantity >= stockLevel
    ) {
      submitButton.disabled = true;
      submitButton.style.opacity = "0.5";
      submitButton.style.cursor = "not-allowed";
      submitButton.setAttribute("data-mossy-stock-disabled", "true");
      submitButton.textContent = "All Stock in Cart";

      console.log(
        `[Mossy] Add-to-cart disabled: ${cartQuantity}/${stockLevel} in cart`,
      );
    } else {
      submitButton.disabled = false;
      submitButton.style.opacity = "1";
      submitButton.style.cursor = "pointer";

      // Restore original button text
      const originalText = submitButton.getAttribute(
        "data-mossy-original-text",
      );
      if (
        originalText &&
        submitButton.hasAttribute("data-mossy-stock-disabled")
      ) {
        submitButton.textContent = originalText;
        submitButton.removeAttribute("data-mossy-stock-disabled");
      }
    }
  });
}

// Export for use in cart.js
export { checkAddToCartButtons };

export function initForms(addToCart) {
  // Check add-to-cart buttons on form mount and cart updates
  checkAddToCartButtons();

  // Listen for cart updates to recheck buttons
  document.addEventListener("mossy-cart-update", checkAddToCartButtons);

  // Listen for router events to recheck buttons after navigation
  document.addEventListener("mossy-router", () => {
    // Use setTimeout to ensure DOM has updated
    setTimeout(checkAddToCartButtons, 10);
  });

  document.addEventListener("submit", async (e) => {
    if (e.target.tagName.toLowerCase() === "form") {
      e.preventDefault();
      const form = e.target;
      const submitButton = form.querySelector(
        'input[type="submit"], button[type="submit"]',
      );

      // Check if button was disabled due to stock limits
      if (
        submitButton &&
        submitButton.hasAttribute("data-mossy-stock-disabled")
      ) {
        console.log("[Mossy] Form submission blocked - all stock in cart");
        return;
      }

      const originalButtonText = submitButton.textContent || submitButton.value;
      submitButton.disabled = true;
      const buttonHeight = submitButton.offsetHeight;
      submitButton.style.height = `${buttonHeight}px`;
      submitButton.style.position = "relative";
      submitButton.innerHTML = `
        <div style="position: absolute; top: 50%; left: 50%; width: 20px; height: 20px; margin: -10px 0 0 -10px; box-sizing: border-box; border: 4px solid transparent; border-top-color: currentColor; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <div style="position: absolute; top: 50%; left: 50%; width: 20px; height: 20px; margin: -10px 0 0 -10px; box-sizing: border-box; border: 4px solid currentColor; border-radius: 50%; opacity: 0.2;"></div>
        <style>
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      `;

      const formResMessage = form.querySelector(
        '[data-mossy-value="response"]',
      );

      const formData = new FormData(form);
      let success = false;

      if (form.getAttribute("data-mossy-form-action") === "add-to-cart") {
        const product = grabClientSideData("product");
        // If no variant, and only one in data, grab it.
        if (!formData.has("variant")) {
          if (product.variants.length === 1) {
            formData.set("variant", product.variants[0].id);
          } else {
            console.log(
              "[Mossy] This product has multiple variants, you must specify which one to add.",
            );
          }
        }
        // Grab variant from client-side data
        const variant = product.variants.find(
          (item) => item.id === formData.get("variant"),
        );

        const requestedQuantity = parseInt(formData.get("quantity"), 10) || 1;

        // Check if adding this quantity would exceed stock
        const cartData = window.mossyCart?.getState?.();
        const cartItems = cartData?.items || [];
        const cartItem = cartItems.find(
          (item) => item.variant.id === variant.id,
        );
        const currentCartQuantity = cartItem ? cartItem.quantity : 0;
        const stockLevel = variant.stock_level;

        if (stockLevel !== null && stockLevel !== undefined) {
          const totalQuantity = currentCartQuantity + requestedQuantity;

          if (totalQuantity > stockLevel) {
            const availableToAdd = stockLevel - currentCartQuantity;

            // Show error message
            if (formResMessage) {
              formResMessage.textContent =
                availableToAdd > 0
                  ? `Only ${availableToAdd} more can be added (${currentCartQuantity} already in cart)`
                  : `All stock already in cart`;
              // formResMessage.style.color = "var(--mossy-error-color, #e74c3c)";
              formResMessage.classList.add("show");
            }

            console.log(
              `[Mossy] Cannot add ${requestedQuantity}. Would exceed stock: ${totalQuantity}/${stockLevel}`,
            );

            // Re-enable button and return without adding to cart
            submitButton.disabled = false;
            submitButton.style.height = "";
            submitButton.style.position = "";
            submitButton.innerHTML = "";
            submitButton.textContent = originalButtonText;

            return;
          }
        }

        // Collect product option values from the form
        const selectedOptions = collectProductOptions(form);

        // Create UUID incase of new item
        const { UUID } = await import("./uuid.js");
        const newUUID = UUID.generate();
        formData.set("id", newUUID);

        // Attach options to formData so the server can persist them
        if (selectedOptions) {
          formData.set("options", JSON.stringify(selectedOptions));
        }

        // Add to cart optimistically (before server confirmation)
        addToCart({
          id: newUUID,
          product,
          variant,
          quantity: requestedQuantity,
          options: selectedOptions,
        });

        // Assume success for optimistic UI
        success = true;

        // Send to server in background (but don't wait for it)
        fetch(`/products/${product.slug}`, {
          method: "post",
          body: formData,
        }).catch((error) => {
          console.error("Error submitting add-to-cart to server:", error);
        });
      } else if (form.getAttribute("data-mossy-action") === "email") {
        // Solve captcha invisibly
        let captchaSolution;
        try {
          if (!form.captchaInstance) {
            throw new Error("Captcha not initialized");
          }

          captchaSolution = await form.captchaInstance.solve();

          if (!captchaSolution || !captchaSolution.token) {
            throw new Error("Captcha solving failed");
          }
        } catch (error) {
          console.error("Error solving captcha:", error);
          formResMessage.textContent =
            "Security verification failed. Please try again.";
          // formResMessage.style.color = "var(--mossy-error-color, #e74c3c)";
          formResMessage.classList.add("show");
          submitButton.disabled = false;
          submitButton.style.height = "";
          submitButton.style.position = "";
          submitButton.innerHTML = "";
          submitButton.textContent = originalButtonText;
          return;
        }

        try {
          // Convert FormData to a plain object
          const formDataObject = {};
          for (let [key, value] of formData.entries()) {
            formDataObject[key] = value;
          }

          // Add captcha token to the submission
          formDataObject["captcha-token"] = captchaSolution.token;

          const response = await fetch(`/api/email`, {
            method: "post",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formDataObject),
          });
          if (!response.ok) throw new Error("Submission failed");

          const result = await response.json();
          success = true;

          // Reset the captcha after successful submission
          form.captchaInstance.reset();
        } catch (error) {
          console.error("Error submitting form:", error);
          formResMessage.textContent = "Something went wrong, try again.";
          // formResMessage.style.color = "var(--mossy-error-color, #e74c3c)";
          formResMessage.classList.add("show");
          // Reset captcha on error too
          form.captchaInstance.reset();
        }
      } else if (form.getAttribute("data-mossy-action") === "emails_subscribe") {
        // Solve captcha invisibly
        let captchaSolution;
        try {
          if (!form.captchaInstance) {
            throw new Error("Captcha not initialized");
          }

          captchaSolution = await form.captchaInstance.solve();

          if (!captchaSolution || !captchaSolution.token) {
            throw new Error("Captcha solving failed");
          }
        } catch (error) {
          console.error("Error solving captcha:", error);
          formResMessage.textContent =
            "Security verification failed. Please try again.";
          // formResMessage.style.color = "var(--mossy-error-color, #e74c3c)";
          formResMessage.classList.add("show");
          submitButton.disabled = false;
          submitButton.style.height = "";
          submitButton.style.position = "";
          submitButton.innerHTML = "";
          submitButton.textContent = originalButtonText;
          return;
        }

        try {
          // Convert FormData to a plain object
          const formDataObject = {};
          for (let [key, value] of formData.entries()) {
            formDataObject[key] = value;
          }

          // Add captcha token to the submission
          formDataObject["captcha-token"] = captchaSolution.token;

          const response = await fetch(`/api/emails/subscribe`, {
            method: "post",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formDataObject),
          });
          if (!response.ok) throw new Error("Submission failed");

          const result = await response.json();

          console.log(result)

          success = true;

          // Reset the captcha after successful submission
          form.captchaInstance.reset();
        } catch (error) {
          console.error("Error submitting form:", error);
          formResMessage.textContent = "Something went wrong, try again.";
          // formResMessage.style.color = "var(--mossy-error-color, #e74c3c)";
          formResMessage.classList.add("show");
          // Reset captcha on error too
          form.captchaInstance.reset();
        }
      } else if (form.getAttribute("data-mossy-action") === "access") {
        try {
          const formDataObject = {};
          for (let [key, value] of formData.entries()) {
            formDataObject[key] = value;
          }

          const response = await fetch("/api/access", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formDataObject),
          });

          if (!response.ok) throw new Error("Incorrect password");

          success = true;
          location.reload();
        } catch (error) {
          if (formResMessage) {
            formResMessage.textContent =
              "Incorrect password. Please try again.";
            // formResMessage.style.color = "var(--mossy-error-color, #e74c3c)";
            formResMessage.classList.add("show");
          }
        }
      } else {
        try {
          const response = await fetch(form.action, {
            method: form.method.toLowerCase(),
            body: form.method === "post" ? formData : undefined,
          });
          if (!response.ok) throw new Error("Submission failed");
          const result = await response.json();
          success = true;
        } catch (error) {
          console.error("Error submitting form:", error);
          formResMessage.textContent = "Something went wrong, try again.";
          // formResMessage.style.color = "var(--mossy-error-color, #e74c3c)";
          formResMessage.classList.add("show");
        }
      }

      // Clear form inputs on successful submission
      if (success) {
        form.reset();

        // Also clear any custom elements that form.reset() might miss
        const customInputs = form.querySelectorAll("[data-custom-input]");
        customInputs.forEach((input) => {
          if (input.tagName === "SELECT") {
            input.selectedIndex = 0;
          } else {
            input.value = "";
          }
        });

        // Handle success actions
        handleSuccessAction(form);

        // Show success message if explicitly provided, otherwise hide
        if (formResMessage) {
          const formResSuccessMessage = form.querySelector(
            "[data-mossy-success-message]",
          );
          if (formResSuccessMessage) {
            formResMessage.textContent =
              formResSuccessMessage.dataset.mossySuccessMessage;
            // formResMessage.style.color = "";
            formResMessage.classList.add("show");
          } else {
            // Hide message on successful add-to-cart with no explicit success message
            formResMessage.classList.remove("show");
          }
        }
      }

      submitButton.disabled = false;
      submitButton.style.height = "";
      submitButton.style.position = "";
      submitButton.innerHTML = "";
      submitButton.textContent = originalButtonText;
    }
  });
}

/* =====================================================================
   PUBLIC CLEANUP
===================================================================== */

export function cleanup() {
  cleanupSliders();
  cleanupMasonryGrids();
}
